// components/mobile/MobileNav.jsx
// Navigasi mobile: bottom tab bar (Home / Boards / AI / More) + sheet slide-up
// untuk daftar dashboard. Navigasi via react-router (URL = sumber kebenaran,
// sama seperti desktop). Difilter izin lewat hasAccess() dari AuthContext.
//
// Dipakai di DashboardContainer HANYA saat useIsMobile() true.
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, LayoutGrid, Sparkles, Menu, X, ChevronRight } from 'lucide-react';
import { ROUTE_META } from '../../routesMeta';
import { BOARD_GROUPS as BOARD_GROUP_NAMES, GROUP_KEY, groupOf } from '../../config/navGroups';
import { useAuth } from '../../context/AuthContext';
import { useLang } from '../../context/LanguageContext';
import { dashboardKeyForPath } from '../../config/dashboardKeys';
import { visualFor } from '../dashboardVisual';
import MobileDrawer from './MobileDrawer';

// Kelompok "Boards" DITURUNKAN dari config/navGroups — sumber yang sama dengan
// sidebar desktop. Sebelumnya daftar ini ditulis tangan di sini dan sudah
// ketinggalan: SAP Roles, Logon Audit, SAProuter Log, Customizing, dan AWS Cost
// Brief tidak pernah ditambahkan, sehingga di mobile kelimanya terlempar ke tab
// "More" bersama setelan admin. Sekarang halaman baru cukup didaftarkan sekali
// di navGroups dan langsung muncul di kedua sisi.
//
// Urutan key dalam grup mengikuti urutan ROUTE_META, yang juga urutan
// tampil di sidebar — jadi posisi menu tidak berpindah antara desktop dan
// mobile.
const BOARD_GROUPS = BOARD_GROUP_NAMES
  .map(g => ({
    group: g,
    labelKey: GROUP_KEY[g],
    keys: ROUTE_META.filter(r => groupOf(r.key) === g).map(r => r.key),
  }))
  .filter(g => g.keys.length);
const HOME_KEY = 'overview';
const AI_KEY   = 'ai_workspace';

// hasAccess(key) sudah menangani admin (selalu true) & '__admin__'.
function canSee(route, hasAccess) {
  if (route.permission === null) return true;
  return hasAccess(route.permission);
}

export default function MobileNav() {
  const nav = useNavigate();
  const loc = useLocation();
  const { hasAccess } = useAuth();
  const { t } = useLang();
  const [sheet, setSheet] = useState(null); // null | 'boards' | 'more'
  const [drawer, setDrawer] = useState(false); // drawer kiri (edge-swipe)
  useEffect(() => { const o = () => setDrawer(true); window.addEventListener('mobile-drawer-open', o); return () => window.removeEventListener('mobile-drawer-open', o); }, []);
  const [drawerPeek, setDrawerPeek] = useState(0); // px drawer mengintip mengikuti jari saat gesture buka
  const [peekHold, setPeekHold] = useState(false); // tahan mount sebentar saat peek batal → transisi balik terlihat
  const sheetOpenRef = useRef(false);          // mirror utk listener global (deps [])
  sheetOpenRef.current = sheet !== null;
  const drawerOpenRef = useRef(false);
  drawerOpenRef.current = drawer;

  // ── Edge-swipe kiri → buka drawer, FOLLOW-FINGER: panel ikut jari sejak awal
  // (peek), lepas > 100px → terbuka penuh; kurang → menyelinap balik. Start di
  // ≤24px dari tepi kiri, horizontal dominan. NONAKTIF saat sheet/drawer sudah
  // terbuka. Catatan: gesture "back" Chrome Android bisa menang duluan.
  useEffect(() => {
    let start = null, tracking = false;
    const reset = () => { start = null; if (tracking) { tracking = false; } };
    const onStart = (e) => {
      if (sheetOpenRef.current || drawerOpenRef.current) { start = null; return; }
      const t = e.touches[0];
      start = (t.clientX <= 24) ? { x: t.clientX, y: t.clientY } : null;
      tracking = false;
    };
    const onMove = (e) => {
      if (!start) return;
      const t = e.touches[0];
      const dx = t.clientX - start.x, dy = Math.abs(t.clientY - start.y);
      if (!tracking) {
        if (dx > 10 && dx > dy * 1.5) tracking = true;   // mulai follow-finger
        else if (dy > 40) { reset(); return; }            // vertikal → scroll biasa
        else return;
      }
      setDrawerPeek(Math.max(0, Math.min(dx, 340)));      // panel ikut jari
    };
    const onEnd = () => {
      if (tracking) {
        setDrawerPeek(p => {
          if (p > 100) { setDrawer(true); }               // cukup jauh → buka penuh
          else if (p > 0) {                                // batal → tahan mount utk transisi balik
            setPeekHold(true);
            setTimeout(() => setPeekHold(false), 260);
          }
          return 0;
        });
      }
      reset();
    };
    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchmove', onMove, { passive: true });
    document.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
    };
  }, []);

  const byKey = Object.fromEntries(ROUTE_META.map(r => [r.key, r]));
  const visible = (key) => byKey[key] && canSee(byKey[key], hasAccess);

  const groupedKeys = new Set(BOARD_GROUPS.flatMap(g => g.keys));
  const moreRoutes = ROUTE_META.filter(r =>
    r.key !== HOME_KEY && r.key !== AI_KEY && !groupedKeys.has(r.key) && canSee(r, hasAccess)
  );

  // Tab "More" dulunya satu daftar rata berisi 20+ baris. Dikelompokkan dengan
  // grup yang sama seperti sidebar supaya bisa dipindai, bukan dibaca satu per
  // satu. Urutan grup mengikuti kemunculan pertamanya di ROUTE_META.
  const moreGroups = [];
  moreRoutes.forEach(r => {
    const g = groupOf(r.key);
    let bucket = moreGroups.find(x => x.group === g);
    if (!bucket) moreGroups.push(bucket = { group: g, routes: [] });
    bucket.routes.push(r);
  });

  const go = (path) => { setSheet(null); nav(path); };
  // Tab AI: bila sedang di halaman dashboard ber-data, bawa key-nya sebagai ?src=
  // agar AI Workspace default ke datasource itu (bukan "Umum"). Dari Home/Overview
  // atau halaman tanpa data → tanpa ?src= (tetap Umum).
  const goAI = () => {
    const aiPath = byKey[AI_KEY].path;
    const activeDash = dashboardKeyForPath(loc.pathname);
    setSheet(null);
    nav(activeDash ? `${aiPath}?src=${encodeURIComponent(activeDash)}` : aiPath);
  };
  const isPath = (p) => loc.pathname === p || (p !== '/dashboard' && loc.pathname.startsWith(p + '/'));
  const boardPaths = BOARD_GROUPS.flatMap(g => g.keys).filter(visible).map(k => byKey[k].path);
  const onBoards = boardPaths.some(p => isPath(p));

  // ── Tutup sheet via gesture: (a) seret ke BAWAH (ikut jari, spt sebelumnya),
  // atau (b) swipe KANAN→KIRI (konsisten dgn cara keluar drawer). Sumbu dikunci
  // ke arah dominan gerakan pertama supaya tidak dobel-tafsir dgn scroll.
  const [dragY, setDragY] = useState(0);
  const [dragX, setDragX] = useState(0);
  const sheetRef  = useRef(null);
  const dragRef   = useRef({ startX: null, startY: null, axis: null, x: 0, y: 0 });
  const sheetEntered = useRef(false); // animasi masuk hanya sekali per buka
  useEffect(() => { if (sheet) sheetEntered.current = false; }, [sheet]);
  const closeSheet = () => { setSheet(null); setDragY(0); setDragX(0); dragRef.current = { startX: null, startY: null, axis: null, x: 0, y: 0 }; };
  const onTouchStart = (e) => {
    const el = sheetRef.current;
    const t = e.touches[0];
    // Sumbu vertikal (drag-down) hanya kandidat bila list di scroll paling atas;
    // sumbu horizontal (swipe-kiri) selalu kandidat.
    dragRef.current = { startX: t.clientX, startY: (el && el.scrollTop <= 0) ? t.clientY : null, axis: null, x: 0, y: 0 };
  };
  const onTouchMove = (e) => {
    const d = dragRef.current;
    if (d.startX == null) return;
    const t = e.touches[0];
    const dx = t.clientX - d.startX;
    const dy = d.startY != null ? t.clientY - d.startY : 0;
    if (!d.axis) {
      if (dx < -14 && Math.abs(dx) > Math.abs(dy)) d.axis = 'x';        // kiri dominan
      else if (d.startY != null && dy > 12 && dy > Math.abs(dx)) d.axis = 'y'; // bawah dominan
      else if (Math.abs(dy) > 24 || dx > 24) { d.startX = null; return; }      // scroll / kanan → biarkan
      else return;
    }
    if (d.axis === 'x') { d.x = Math.min(0, dx); setDragX(d.x); }
    else                { d.y = Math.max(0, dy); setDragY(d.y); }
  };
  const onTouchEnd = () => {
    const d = dragRef.current;
    if ((d.axis === 'y' && d.y > 90) || (d.axis === 'x' && d.x < -70)) { closeSheet(); return; }
    dragRef.current = { startX: null, startY: null, axis: null, x: 0, y: 0 };
    setDragY(0); setDragX(0);
  };

  const Tab = ({ label, icon: Icon, active, onClick }) => (
    <button onClick={onClick} aria-label={label}
      className={`flex flex-col items-center justify-center flex-1 py-1.5 gap-0.5 ${active ? 'text-blue-600' : 'text-slate-400'}`}>
      <Icon style={{ width: 20, height: 20 }} />
      <span className="text-[10px] leading-none">{label}</span>
    </button>
  );

  return (
    <>
      {/* Drawer kiri (edge-swipe follow-finger dari tepi kiri) */}
      <MobileDrawer open={drawer} peek={drawerPeek} hold={peekHold} onClose={() => setDrawer(false)} />

      {/* Sheet daftar dashboard */}
      {sheet && (
        <div className="fixed inset-0 z-[850] md:hidden" role="dialog" aria-modal="true">
          {/* Efek masuk: backdrop fade + sheet slide dari bawah (CSS murni) */}
          <style>{`
            @keyframes pdSheetFade { from { opacity: 0; } to { opacity: 1; } }
            @keyframes pdSheetSlide { from { transform: translateY(100%); } to { transform: translateY(0); } }
          `}</style>
          <div className="absolute inset-0 bg-slate-900/40" onClick={closeSheet}
               style={{ animation: 'pdSheetFade 0.2s ease both' }} />
          <div ref={sheetRef}
               className="absolute inset-x-0 bottom-0 bg-white rounded-t-2xl shadow-xl max-h-[80vh] overflow-y-auto"
               onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
               style={{
                 paddingBottom: 'max(env(safe-area-inset-bottom), 12px)',
                 transform: `translate(${dragX}px, ${dragY}px)`,
                 transition: (dragX === 0 && dragY === 0) ? 'transform 0.2s ease' : 'none',
                 touchAction: (dragX < 0 || dragY > 0) ? 'none' : 'pan-y',
                 animation: sheetEntered.current ? 'none' : 'pdSheetSlide 0.25s cubic-bezier(0.16, 1, 0.3, 1) both',
               }}
               onAnimationEnd={() => { sheetEntered.current = true; }}>
            <div className="sticky top-0 bg-white border-b border-slate-100 px-4 pt-3 pb-2 flex items-center justify-between">
              <button onClick={closeSheet} aria-label="Tutup"
                className="mx-auto w-10 h-1.5 rounded-full bg-slate-300 absolute left-1/2 -translate-x-1/2 top-1.5" />
              <h3 className="font-semibold text-slate-800 text-base mt-1">{sheet === 'boards' ? 'Dashboards' : 'Menu'}</h3>
              <button onClick={closeSheet} aria-label="Close" className="p-2 -mr-2 text-slate-400"><X style={{ width: 20, height: 20 }} /></button>
            </div>

            {sheet === 'boards' ? (
              <div className="px-3 py-2">
                {BOARD_GROUPS.map(g => {
                  const items = g.keys.filter(visible);
                  if (!items.length) return null;
                  return (
                    <div key={g.group} className="mb-3">
                      <p className="px-2 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">{t(g.labelKey)}</p>
                      {items.map(k => (
                        <Row key={k} routeKey={k} label={byKey[k].label} active={isPath(byKey[k].path)} onClick={() => go(byKey[k].path)} />
                      ))}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="px-3 py-2">
                {moreRoutes.length === 0
                  ? <p className="px-3 py-6 text-center text-sm text-slate-400">Tidak ada menu lain.</p>
                  : moreGroups.map(g => (
                      <div key={g.group} className="mb-3">
                        <p className="px-2 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">{t(GROUP_KEY[g.group] || g.group)}</p>
                        {g.routes.map(r => (
                          <Row key={r.key} routeKey={r.key} label={r.label} active={isPath(r.path)} onClick={() => go(r.path)} />
                        ))}
                      </div>
                    ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bottom tab bar */}
      <nav className="fixed bottom-0 inset-x-0 z-[800] bg-white border-t border-slate-200 flex md:hidden"
           style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <Tab label="Home" icon={Home} active={isPath('/dashboard')} onClick={() => go('/dashboard')} />
        {boardPaths.length > 0 && (
          <Tab label="Boards" icon={LayoutGrid} active={onBoards} onClick={() => setSheet('boards')} />
        )}
        {visible(AI_KEY) && (
          <Tab label="AI" icon={Sparkles} active={isPath(byKey[AI_KEY].path)} onClick={goAI} />
        )}
        <Tab label="More" icon={Menu} active={sheet === 'more'} onClick={() => setSheet('more')} />
      </nav>
    </>
  );
}

function Row({ routeKey, label, active, onClick }) {
  const v = visualFor(routeKey);
  const Icon = v.icon;
  return (
    <button onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm ${active ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-slate-700 active:bg-slate-50'}`}>
      <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${active ? 'bg-white' : v.bgColor}`}>
        <Icon className={v.color} style={{ width: 16, height: 16 }} />
      </span>
      <span className="flex-1 text-left">{label}</span>
      <ChevronRight style={{ width: 16, height: 16 }} className={active ? 'text-blue-400' : 'text-slate-300'} />
    </button>
  );
}
